// Typed wrappers for /v1/merchants/:id/ledger-entries (payouts-backend#9).
//
// Backs merchants.balance_display='ledger' — operator-entered credits/debits
// that the dashboard balance card computes against successful payouts.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient.
import { apiClient } from "./client";

/**
 * LedgerEntryKind — matches the CHECK constraint at migration 042.
 *
 *   "opening"    — the balance this merchant started with on our books.
 *                  Renamed from "baseline", which named the mechanism ("the
 *                  first number typed") rather than the thing.
 *   "topup"      — we credited them (positive).
 *   "payout"     — they paid someone (negative). Written by the system.
 *   "fee"        — we charged them (negative). Written by the system.
 *   "refund"     — money came back (positive). Written by the system.
 *   "adjustment" — signed manual correction.
 *
 * Sign is NOT implied by kind: amount_minor is signed either way.
 */
export type LedgerEntryKind =
  | "opening"
  | "topup"
  | "payout"
  | "fee"
  | "refund"
  | "adjustment";

/** Kinds an operator may post.
 *
 * 'fee' is included: a one-off charge an operator decides on — chargeback
 * recovery, an agreed penalty, anything the fee scale doesn't cover. It
 * shows under the same label as an automatic fee so the merchant reads one
 * consistent line item; a manual one just carries no fee_rule_id or ref.
 *
 * 'payout' and 'refund' are excluded. They assert money moved through a
 * rail, and a hand-typed one would back no actual payment in a table that is
 * append-only and cannot be corrected by deletion. */
export const OPERATOR_LEDGER_KINDS: LedgerEntryKind[] = [
  "opening",
  "topup",
  "fee",
  "adjustment",
];

/** Human labels. The wire value is the DB enum; this is what an operator
 *  reading a statement should see. */
export const LEDGER_KIND_LABEL: Record<LedgerEntryKind, string> = {
  opening: "Opening balance",
  topup: "Top-up",
  payout: "Payout",
  fee: "Fee",
  refund: "Refund",
  adjustment: "Adjustment",
};

/**
 * LedgerEntry — append-only row from merchant_ledger_entries.
 *
 * amount_minor is INR paise. The apiClient's JSON-bigint codec parses any
 * value above Number.MAX_SAFE_INTEGER as bigint; smaller amounts arrive as
 * number. Type both cases (matches the convention in admin.ts). At call
 * sites, BigInt(amount_minor) coerces either form to bigint when needed
 * (e.g. for fmtCcyMinor which is bigint-only).
 */
export interface LedgerEntry {
  id: number;
  merchant_id: number;
  kind: LedgerEntryKind;
  amount_minor: number | bigint;
  currency: string;
  note: string | null;
  actor_user_id: number | null;
  /** Provenance for system-written rows (migration 042): which payout this
   *  is, and which fee band charged it. Absent on operator entries. */
  payout_id?: number;
  fee_rule_id?: number;
  /** Idempotency key for system-written rows. Absent on operator entries,
   *  which are deliberately repeatable. */
  ref?: string;
  created_at: string;
}

/**
 * CreateLedgerEntryPayload — POST body. amount_minor is signed (positive
 * for credits, either direction for adjustments). Zero is rejected at the
 * handler with 400. Number is fine on the wire: even ₹90 trillion (the
 * Number.MAX_SAFE_INTEGER boundary in paise) is implausible for a single
 * ledger entry.
 */
export interface CreateLedgerEntryPayload {
  kind: LedgerEntryKind;
  amount_minor: number;
  note?: string;
}

/**
 * listLedgerEntries — GET /v1/merchants/:merchantId/ledger-entries
 * Returns up to 500 entries, newest first.
 */
export async function listLedgerEntries(
  merchantId: number | string,
): Promise<LedgerEntry[]> {
  const resp = await apiClient.get<{ ledger_entries: LedgerEntry[] }>(
    `/v1/merchants/${merchantId}/ledger-entries`,
  );
  return resp.data.ledger_entries;
}

/**
 * createLedgerEntry — POST /v1/merchants/:merchantId/ledger-entries
 * Admin-only.
 */
export async function createLedgerEntry(
  merchantId: number | string,
  payload: CreateLedgerEntryPayload,
): Promise<LedgerEntry> {
  const resp = await apiClient.post<LedgerEntry>(
    `/v1/merchants/${merchantId}/ledger-entries`,
    payload,
  );
  return resp.data;
}
